import type { EnhancedStore } from "@reduxjs/toolkit"
import ky, {
  HTTPError,
  type Input as KyInput,
  type Options as KyOptions,
  type SearchParamsOption,
} from "ky"
import type { OnErrorFunc, RequestParams } from "./types"
import { LOGOUT_RTK } from "./components/access/ducks/Actions"

declare module "ky" {
  interface HTTPError {
    description?: string
  }
}

let httpAgent = ky.create({
  credentials: "same-origin",
  prefixUrl: "/api",
  headers: { accept: "application/json; charset=utf8" },
  redirect: "follow",
  timeout: 15000, // 15 seconds
  hooks: {
    beforeError: [
      async (error) => {
        try {
          const body: Record<string, unknown> = await error.response.json()
          const description = body?.description
          if (typeof description === "string") {
            error.description = description
          }
        } catch {
          // ponytail: non-JSON or empty body — description stays undefined, getErrorText falls back to "Oops"
        }
        return error
      },
    ],
  },
})

const NO_CSRF = ["GET", "HEAD", "OPTIONS", "TRACE"]

export const initHttpAgent = (token: string, store: EnhancedStore) => {
  httpAgent = httpAgent.extend({
    hooks: {
      beforeRequest: [
        (req, _opts) => {
          if (NO_CSRF.includes(req.method)) return

          req.headers.set("X-CSRFToken", token)
        },
      ],
      afterResponse: [
        async (_req, _opts, resp) => {
          if (resp.status === 307) {
            // if a temporary redirect is received, check for redirectUrl as it will probably be manually triggered
            const json: Record<string, unknown> = await resp.json()
            if (json.redirectUrl) {
              window.location.href = json.redirectUrl as string
            }
          }

          if (resp.status === 400) {
            const json: Record<string, unknown> = await resp.json()
            if (json?.type === "logout") {
              store.dispatch(LOGOUT_RTK())
            }
          }
        },
      ],
    },
  })
}

export const http = {
  get: <T>(url: KyInput, options?: KyOptions) => httpAgent.get<T>(url, options),
  post: <T>(url: KyInput, options?: KyOptions) => httpAgent.post<T>(url, options),
  put: <T>(url: KyInput, options?: KyOptions) => httpAgent.put<T>(url, options),
  delete: <T>(url: KyInput, options?: KyOptions) => httpAgent.delete<T>(url, options),
}

export const errHof =
  (onErr: OnErrorFunc, name: string, rejectWithValue?: OnErrorFunc): OnErrorFunc =>
  (err) => {
    if (err instanceof HTTPError) {
      console.error(`${name}: ${err.message}`)

      onErr(err)
    }

    if (rejectWithValue) {
      rejectWithValue(err)
    } else {
      throw err
    }
  }

const handleError = (err: unknown, errHandler?: OnErrorFunc) => {
  if (err instanceof HTTPError) {
    if (errHandler) {
      errHandler(err)
    } else {
      console.error(err)
    }
  } else {
    throw err
  }
}

export const httpGet = async <T, Params extends SearchParamsOption = SearchParamsOption>(
  url: string,
  params: Params,
  { success, error, signal }: RequestParams<T>,
): Promise<T | undefined> => {
  try {
    const res = await httpAgent.get(url, { searchParams: params, signal })

    const data: T = await res.json()

    if (success) await success(data)

    return data
  } catch (err) {
    handleError(err, error)
  }
}

export const httpPost = async <T, U extends Record<string, unknown> = Record<string, never>>(
  url: string,
  json: U,
  { success, error, signal }: RequestParams<T>,
) => {
  try {
    const res = await httpAgent.post(url, { json, signal })
    const data: T = await res.json()

    if (success) await success(data)

    return data
  } catch (err) {
    handleError(err, error)
    throw err
  }
}

export const httpPut = async <T, U = Record<string, never>>(
  url: string,
  json: U,
  { success, error, signal }: RequestParams<T>,
) => {
  try {
    const res = await httpAgent.put(url, { json, signal })
    const data: T = await res.json()

    if (success) await success(data)

    return data
  } catch (err) {
    handleError(err, error)
  }
}

export const httpDelete = async <T, U extends SearchParamsOption = Record<string, never>>(
  url: string,
  params: U,
  { success, error, signal }: RequestParams<T>,
) => {
  try {
    const res = await httpAgent.delete(url, { searchParams: params, signal })
    const data: T = await res.json()

    if (success) await success(data)

    return data
  } catch (err) {
    handleError(err, error)
  }
}

export default httpAgent
